/**
 * L4 — data confidence breakdown drawer with pillar drill-down.
 */
import React, { useState } from 'react';
import { X, RefreshCw } from 'lucide-react';
import type { SiteComposite } from '../../types/portfolioComposite';
import { Button } from '../ui/button';
import { useStore } from '../../store';
import { TooltipProvider } from '../ui/tooltip';
import { CalculatedMetricHint } from '../metrics/CalculatedMetricTooltip';
import {
  explainDataConfidenceDrawerHeader,
  explainDataConfidencePenaltyRow,
  explainPillarDrillDown,
  pillarDrillDownRows,
  type PenaltyKey,
} from '../../lib/metrics/dataConfidenceExplanations';
import { formatScore2Dec } from '../../lib/portfolio/format';
import { pillarRiskClasses, pillarRiskLevel } from '../../lib/portfolio/portfolioViewHelpers';

export interface DataConfidenceDrawerProps {
  composite: SiteComposite | null;
  siteId?: string;
  open: boolean;
  onClose: () => void;
}

function PenaltyRow({
  composite,
  penaltyKey,
  label,
}: {
  composite: SiteComposite;
  penaltyKey: PenaltyKey;
  label: string;
}) {
  const b = composite.dataConfidenceBreakdown;
  const p = b.penalties;
  const counts: Record<PenaltyKey, number> = {
    missingRequired: b.missingRequiredProviders,
    missingRecommended: b.missingRecommendedProviders,
    danglingCitations: b.danglingCitations,
    provenanceGaps: b.provenanceGaps,
    distributionOutliers: b.distributionOutliers,
    reconciliationBlocks: b.reconciliationBlocks,
    unresolvedHighConflicts: b.unresolvedHighConflicts ?? 0,
  };
  const count = counts[penaltyKey];
  const penalty = p[penaltyKey] ?? 0;
  const explanation = explainDataConfidencePenaltyRow(penaltyKey, composite);

  return (
    <div className="flex justify-between items-center text-xs py-1 border-b border-border-subtle/50 gap-2">
      <span className="text-text-secondary inline-flex items-center">
        {label}
        <CalculatedMetricHint explanation={explanation} testId={`dc-row-${penaltyKey}`} />
      </span>
      <span className="font-mono shrink-0">{count} (+{penalty.toFixed(2)})</span>
    </div>
  );
}

export function DataConfidenceDrawer({ composite, siteId, open, onClose }: DataConfidenceDrawerProps) {
  const [refetching, setRefetching] = useState(false);
  const refreshSedSections = useStore(s => s.refreshSedSections);

  if (!open || !composite) return null;

  const b = composite.dataConfidenceBreakdown;
  const missingRequired = b.missingRequiredProviders;
  const headerExplanation = explainDataConfidenceDrawerHeader(composite);
  const pillarRows = pillarDrillDownRows(composite);

  const handleRefetch = async () => {
    if (!siteId) return;
    setRefetching(true);
    try {
      const { SED_SECTION_IDS } = await import('../../types/siteEvidence');
      await refreshSedSections(siteId, [...SED_SECTION_IDS]);
    } catch (err) {
      console.warn('[DataConfidenceDrawer] refetch failed:', (err as Error)?.message);
    } finally {
      setRefetching(false);
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end"
      role="dialog"
      aria-label="Data confidence breakdown"
      data-testid="data-confidence-drawer"
    >
      <div className="bg-background w-full max-w-md h-full overflow-auto border-l border-border">
        <div className="flex items-center justify-between p-4 border-b border-border sticky top-0 bg-background">
          <div>
            <h4 className="text-sm font-bold uppercase tracking-widest inline-flex items-center">
              Data confidence
              <CalculatedMetricHint explanation={headerExplanation} testId="dc-drawer-header-help" />
            </h4>
            <p className="text-[11px] text-text-secondary">
              Tag {composite.confidenceTag} · penalty {composite.dataCompletenessPenalty.toFixed(2)}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="w-4 h-4" />
          </Button>
        </div>

          <div className="p-4 space-y-4">
            <section>
              <h5 className="text-[10px] uppercase tracking-widest text-text-muted mb-2">Provider coverage</h5>
              <PenaltyRow composite={composite} penaltyKey="missingRequired" label="Missing required" />
              <PenaltyRow composite={composite} penaltyKey="missingRecommended" label="Missing recommended" />
            </section>

            <section>
              <h5 className="text-[10px] uppercase tracking-widest text-text-muted mb-2">Citation integrity</h5>
              <PenaltyRow composite={composite} penaltyKey="danglingCitations" label="Dangling citations" />
              <PenaltyRow composite={composite} penaltyKey="provenanceGaps" label="Provenance gaps" />
            </section>

            <section>
              <h5 className="text-[10px] uppercase tracking-widest text-text-muted mb-2">Quality gates</h5>
              <PenaltyRow composite={composite} penaltyKey="distributionOutliers" label="Distribution outliers" />
              <PenaltyRow composite={composite} penaltyKey="reconciliationBlocks" label="Reconciliation blocks" />
              <PenaltyRow composite={composite} penaltyKey="unresolvedHighConflicts" label="Unresolved high-severity conflicts" />
            </section>

            <section data-testid="dc-pillar-drilldown">
              <h5 className="text-[10px] uppercase tracking-widest text-text-muted mb-2">
                Pillar scores (portfolio composite inputs)
              </h5>
              <p className="text-[10px] text-text-secondary mb-2">
                Composite {formatScore2Dec(composite.composite)} after penalty −{composite.dataCompletenessPenalty.toFixed(2)}.
              </p>
              <ul className="space-y-1">
                {pillarRows.map(row => {
                  const inactive = row.applicability === 'not-applicable';
                  const level = pillarRiskLevel(parseFloat(row.score ?? '') || 0);
                  const cls = inactive
                    ? 'border-border-subtle bg-background-elevated text-text-muted'
                    : pillarRiskClasses(level);
                  const explanation = explainPillarDrillDown(row.pillarId, composite);
                  return (
                    <li
                      key={row.pillarId}
                      className={`flex items-center justify-between gap-2 text-xs px-2 py-1.5 border ${cls}`}
                      data-testid={`dc-pillar-${row.pillarId}`}
                      data-applicability={row.applicability}
                    >
                      <span className="truncate inline-flex items-center min-w-0">
                        <span className="truncate">{row.label}</span>
                        {row.missing && !inactive && <span className="ml-1 text-[9px] uppercase opacity-70">missing</span>}
                        <CalculatedMetricHint explanation={explanation} testId={`dc-pillar-${row.pillarId}-help`} />
                      </span>
                      {row.score != null ? <span className="font-mono shrink-0">{row.score}</span> : null}
                    </li>
                  );
                })}
              </ul>
            </section>

            {missingRequired > 0 && siteId && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={refetching}
                onClick={() => void handleRefetch()}
                data-testid="data-confidence-refetch-cta"
                className="w-full text-[10px] uppercase tracking-widest"
              >
                <RefreshCw className={`w-3 h-3 mr-1.5 ${refetching ? 'animate-spin' : ''}`} />
                Refetch missing data
              </Button>
            )}
          </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
